// src/views/Tarjetas/NuevaTarjeta/SolicitarTarjeta.jsx
import React from "react";
import { FaCreditCard, FaChevronDown, FaChevronUp } from "react-icons/fa";
import TarjetaCliente from "./TarjetaCliente";
import TarjetaMaestra from "./TarjetaMaestra";
import TarjetaPrepaga from "./TarjetaPrepaga";
import useEscClose from "../../useEscClose";
import { hasScope } from "../../../utils/auth";

function AccordionItem({
  id,
  title,
  openId,
  setOpenId,
  children,
  className,
  headerRef,
}) {
  const isOpen = openId === id;
  const toggle = () => setOpenId(isOpen ? null : id);

  return (
    <div className="rounded-2xl border border-gray-200 bg-white shadow-sm mb-4">
      <div
        ref={headerRef}
        className="table-head px-4 py-3 cursor-pointer select-none flex items-center justify-between rounded-2xl"
        role="button"
        tabIndex={0}
        aria-expanded={isOpen}
        aria-controls={`${id}-content`}
        onClick={toggle}
        onKeyDown={(e) =>
          (e.key === "Enter" || e.key === " ") && (e.preventDefault(), toggle())
        }
      >
        <div className="flex items-center gap-3">
          <span className="opacity-90">
            <FaCreditCard />
          </span>
          <h3 className={className ?? "text-base font-semibold"}>{title}</h3>
        </div>
        <span className="opacity-90">
          {isOpen ? <FaChevronUp /> : <FaChevronDown />}
        </span>
      </div>

      <div
        id={`${id}-content`}
        className={`px-4 pb-4 ${isOpen ? "block" : "hidden"}`}
      >
        <div className="rounded-xl border border-dashed border-gray-200 p-4 bg-gray-50">
          {children}
        </div>
      </div>
    </div>
  );
}

export default function SolicitarTarjeta() {
  // ===== Scopes por tipo de tarjeta =====
  const canTarjetaCliente = hasScope("tarjeta cliente:insert");
  const canTarjetaMaestra = hasScope("tarjeta maestra:insert");
  const canTarjetaPrepaga = hasScope("tarjeta pre-paga:insert");

  const tieneAlgunaTarjeta =
    canTarjetaCliente || canTarjetaMaestra || canTarjetaPrepaga;

  // Mensaje de éxito / error
  const [flash, setFlash] = React.useState(null); // { type: 'success'|'error', text: string } | null

  // 🔒 Bloqueo de ESC controlado por el hijo (TarjetaCliente)
  const [escLocked, setEscLocked] = React.useState(false);

  // Recibe la respuesta del backend desde el hijo (TarjetaResponseDto)
  const onGuardarTarjetaCliente = (resp) => {
    const numero = resp?.tarjetaNumero ?? resp?.numero ?? "";
    const msg = numero
      ? `Solicitud creada. Tarjeta: ${numero}`
      : "Solicitud creada correctamente.";
    setFlash({ type: "success", text: msg });
    // Mantener la vista abierta en el hijo
    return false;
  };

  const [openId, setOpenId] = React.useState(null); // <-- cerrado al entrar
  const [tipoSeleccionado, setTipoSeleccionado] = React.useState(null); // 'cliente' | 'maestra' | 'prepaga' | null

  const headerRef = React.useRef(null);

  const scrollAlHeader = React.useCallback(() => {
    requestAnimationFrame(() => {
      headerRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
      document.activeElement?.blur();
    });
  }, []);

  // Volver al menú de botones
  const volverAlMenu = React.useCallback(() => {
    setTipoSeleccionado(null);
    scrollAlHeader();
  }, [scrollAlHeader]);

  // (Opcional pero útil): si se cierra el acordeón desde el header, volver al menú
  React.useEffect(() => {
    if (openId === null) setTipoSeleccionado(null);
  }, [openId]);

  // ESC: si está bloqueado, no hacer nada
  const onEsc = React.useCallback(() => {
    if (escLocked) return; // ⛔ bloqueado por el hijo
    if (tipoSeleccionado !== null) {
      volverAlMenu();
      return;
    }
    if (openId) {
      setOpenId(null);
      scrollAlHeader();
    }
  }, [escLocked, tipoSeleccionado, openId, volverAlMenu, scrollAlHeader]);

  // Habilitar/deshabilitar escucha de ESC según el lock
  useEscClose(onEsc, !escLocked);

  // Cleanup defensivo: si se desmonta, liberar el lock
  React.useEffect(() => {
    return () => setEscLocked(false);
  }, []);

  // Si el usuario no tiene ningún scope de tarjetas nuevas, mostramos un mensaje simple
  if (!tieneAlgunaTarjeta) {
    return (
      <div className="page">
        <h1 className="page-title">💳 Solicitar una nueva Tarjeta</h1>
        <div className="mt-4 rounded-md border border-yellow-200 bg-yellow-50 p-3 text-yellow-800">
          No tenés permisos para solicitar nuevas tarjetas.
        </div>
      </div>
    );
  }

  return (
    <div className="page">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h1 className="page-title">💳 Solicitar una nueva Tarjeta</h1>
        </div>
      </div>
      {flash && (
        <div
          className={
            flash.type === "success"
              ? "mb-4 rounded-md border border-green-200 bg-green-50 p-3 text-green-700"
              : "mb-4 rounded-md border border-red-200 bg-red-50 p-3 text-red-700"
          }
        >
          <div className="flex items-center justify-between gap-3">
            <span>{flash.text}</span>
            <button
              type="button"
              className="btn-fil px-3 py-1"
              onClick={() => {
                setFlash(null);
                setEscLocked(false); // 🔓 por si seguía bloqueado
                volverAlMenu();
              }}
            >
              Cerrar
            </button>
          </div>
        </div>
      )}

      <div className="text-2xl max-w-5xl">
        <AccordionItem
          id="cliente"
          className="text-2xl"
          title="Nueva Tarjeta"
          openId={openId}
          setOpenId={setOpenId}
          headerRef={headerRef}
        >
          {tipoSeleccionado === null && (
            <div className="text-xl text-gray-700 space-y-2">
              <ul className="list-disc pl-5 text-gray-600 space-y-2">
                {canTarjetaCliente && (
                  <li>
                    <button
                      type="button"
                      onClick={() => setTipoSeleccionado("cliente")}
                      className="text-green-700 hover:underline font-medium"
                    >
                      Tarjeta Cliente
                    </button>
                  </li>
                )}
                {canTarjetaMaestra && (
                  <li>
                    <button
                      type="button"
                      onClick={() => setTipoSeleccionado("maestra")}
                      className="text-green-700 hover:underline font-medium"
                    >
                      Tarjeta Maestra
                    </button>
                  </li>
                )}
                {canTarjetaPrepaga && (
                  <li>
                    <button
                      type="button"
                      onClick={() => setTipoSeleccionado("prepaga")}
                      className="text-green-700 hover:underline font-medium"
                    >
                      Tarjeta Pre-Paga
                    </button>
                  </li>
                )}
                {!canTarjetaCliente &&
                  !canTarjetaMaestra &&
                  !canTarjetaPrepaga && (
                    <li className="text-gray-500">
                      No tenés permisos para crear tarjetas.
                    </li>
                  )}
              </ul>
            </div>
          )}

          {tipoSeleccionado === "cliente" && canTarjetaCliente && (
            <div className="space-y-3">
              <div className="flex justify-end">
                <button
                  type="button"
                  className="btn-fil"
                  onClick={() => {
                    setEscLocked(false);
                    volverAlMenu();
                  }}
                >
                  ← Volver
                </button>
              </div>
              <TarjetaCliente
                onClose={volverAlMenu}
                onGuardar={onGuardarTarjetaCliente}
                onEscLockChange={setEscLocked}
              />
            </div>
          )}

          {tipoSeleccionado === "maestra" && canTarjetaMaestra && (
            <div className="space-y-3">
              <div className="flex justify-end">
                <button
                  type="button"
                  className="btn-fil"
                  onClick={() => {
                    setEscLocked(false);
                    volverAlMenu();
                  }}
                >
                  ← Volver
                </button>
              </div>
              <TarjetaMaestra
                onClose={() => {
                  setEscLocked(false);
                  volverAlMenu();
                }}
                onGuardar={onGuardarTarjetaCliente}
              />
            </div>
          )}

          {tipoSeleccionado === "prepaga" && canTarjetaPrepaga && (
            <div className="space-y-3">
              <div className="flex justify-end">
                <button
                  type="button"
                  className="btn-fil"
                  onClick={() => {
                    setEscLocked(false);
                    volverAlMenu();
                  }}
                >
                  ← Volver
                </button>
              </div>
              <TarjetaPrepaga
                onClose={() => {
                  setEscLocked(false);
                  volverAlMenu();
                }}
              />
            </div>
          )}
        </AccordionItem>
      </div>
    </div>
  );
}
